import React, { useMemo } from "react";
import { Container, Graphics } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import {
  usePrimaryUIToolbarDimensionsV2,
  TOOLBAR_FADE_WIDTH,
} from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import CompanyLogo from "../CompanyLogo/CompanyLogo";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

// Shared toolbar background styling
export const TOOLBAR_BACKGROUND_COLOR = 0x090f41;
export const TOOLBAR_BACKGROUND_OPACITY = 0.6;

// Top toolbar height multipliers
const TOOLBAR_TOP_HEIGHT_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.DESKTOP]: 1.0,
};

/**
 * Draws a toolbar background rectangle with faded edges.
 * Exported so individual toolbars can reuse it in "front" mode.
 * @param {string} toolbarColor - "color" = solid background, "transparent" = no background
 */
export const drawToolbarBackground = (g, width, height, opacity, toolbarColor = "color") => {
  g.clear();

  // Skip drawing if transparent mode
  if (toolbarColor === "transparent") return;

  g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity);
  g.drawRect(0, 0, width, height);
  g.endFill();

  const fadeWidth = TOOLBAR_FADE_WIDTH;

  // Left fade — cosine curve: smooth at both ends (no seam, no cutoff)
  for (let i = 0; i < fadeWidth; i++) {
    const t = i / fadeWidth; // 0 → 1 (distance from toolbar edge)
    const eased = (1 + Math.cos(Math.PI * t)) / 2; // 1 → 0 smoothly
    g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity * eased);
    g.drawRect(-i - 1, 0, 1, height);
    g.endFill();
  }

  // Right fade — cosine curve (mirrored)
  for (let i = 0; i < fadeWidth; i++) {
    const t = i / fadeWidth;
    const eased = (1 + Math.cos(Math.PI * t)) / 2;
    g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity * eased);
    g.drawRect(width + i, 0, 1, height);
    g.endFill();
  }
};

const topIsBack = FEATURES_CONFIG.TOOLBAR.TOP.BACKGROUND_MODE === "back";
const bottomIsBack = FEATURES_CONFIG.TOOLBAR.BOTTOM.BACKGROUND_MODE === "back";

/**
 * ToolbarBackgrounds — renders ONLY the colored background rectangles
 * for toolbars configured with BACKGROUND_MODE: "back".
 * Rendered behind GameContainer (low zIndex) so game
 * effects can visually bleed over the toolbar backgrounds.
 */
const ToolbarBackgrounds = () => {
  const toolbar = usePrimaryUIToolbarDimensionsV2();
  const { dimensions, device, orientation, getResponsiveConfig } =
    useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Top toolbar dimensions
  const topToolbar = useMemo(() => {
    const heightMultiplier =
      device === PixiSettings.DEVICE.DESKTOP
        ? TOOLBAR_TOP_HEIGHT_CONFIG[device]
        : (TOOLBAR_TOP_HEIGHT_CONFIG[device]?.[orientation] ?? 0.9);
    return {
      width: toolbar.width,
      height: buttonConfig.height * heightMultiplier,
    };
  }, [toolbar.width, device, orientation, buttonConfig.height]);

  const toolbarX = (dimensions.width - toolbar.width) / 2;
  const topToolbarX = (dimensions.width - topToolbar.width) / 2;

  const bottomY = isMobilePortrait
    ? dimensions.height - toolbar.height * 2 - 8
    : dimensions.height - toolbar.height;

  const bottomOpacity = isMobilePortrait ? 0.35 : TOOLBAR_BACKGROUND_OPACITY;

  return (
    <Container name="toolbar_backgrounds" zIndex={1}>
      {/* Top toolbar background — only when mode is "back" */}
      {topIsBack && (
        <>
          <Graphics
            name="toolbar_top_bg"
            x={topToolbarX}
            y={0}
            draw={(g) =>
              drawToolbarBackground(
                g,
                topToolbar.width,
                topToolbar.height,
                TOOLBAR_BACKGROUND_OPACITY,
                FEATURES_CONFIG.TOOLBAR.TOP.TOOLBAR_COLOR,
              )
            }
          />

          {/* Company logo — in background layer so game effects render on top */}
          <Container x={topToolbarX} y={0}>
            <CompanyLogo toolbar={topToolbar} />
          </Container>
        </>
      )}

      {/* Bottom toolbar background — only when mode is "back" */}
      {bottomIsBack && (
        <Graphics
          name="toolbar_bottom_bg"
          x={toolbarX}
          y={bottomY}
          draw={(g) =>
            drawToolbarBackground(g, toolbar.width, toolbar.height, bottomOpacity, FEATURES_CONFIG.TOOLBAR.BOTTOM.TOOLBAR_COLOR)
          }
        />
      )}

      {/* Bottom second toolbar background (mobile portrait only) — follows bottom mode */}
      {bottomIsBack && isMobilePortrait && (
        <Graphics
          name="toolbar_bottom_second_bg"
          x={toolbarX}
          y={dimensions.height - toolbar.height}
          draw={(g) =>
            drawToolbarBackground(
              g,
              toolbar.width,
              toolbar.height,
              TOOLBAR_BACKGROUND_OPACITY,
              FEATURES_CONFIG.TOOLBAR.BOTTOM.TOOLBAR_COLOR,
            )
          }
        />
      )}
    </Container>
  );
};

export default ToolbarBackgrounds;
